// Angular
import { Injectable } from '@angular/core';

// Third party
import { Observable } from 'rxjs'
import * as moment from 'moment';

// Local library
import {
  IACHRequest,
  IAddACHAccountRequest,
  IAffiliateBalanceResponseModel,
  IAffiliateDepositResponseModel,
  IAffiliateWithdrawRequest,
  IAffiliateWithdrawResponseModel,
  IAmWestDeletePrefRequest,
  IAmWestDeletePrefResponse,
  IBaseResponseSlim,
  ICryptoDepositRequest,
  ICryptoDepositResponse,
  IDeleteACHAccountRequest,
  IGetAccountReportExRequest,
  IGetAccountStatementReportResponse,
  IGetAllACHAccountsResponse,
  IGetCustomerLimitResponse,
  IGetDutchAccountReportExRequest,
  IGetDutchParentStatementReportResponseEx,
  IGetDutchParentTicketsReportResponseEx,
  IGetFileUploadReportExRequest,
  IGetFileUploadStatementReportResponseEx,
  IGetFileUploadTicketsReportResponseEx,
  IGetFileUploadTicksetReportExRequest,
  IGetOnGoingFileUploadsResponse,
  IGetToteAccountAddress,
  IGetUploadedFilesRequest,
  IGetUploadedFilesResponse,
  IGetWagerReportRequest,
  IGetWagerReportResponse,
  IManualTransactionRequest,
  IMoneyPakDepositRequest,
  INewAccountRequest,
  IResponseModel,
  IToteAccountBalanceResponse,
  IToteAccountCallResponse,
  IToteAddAccountCallResponse,
  IUpdateACHAccountRequest,
  IUpdatePasswordRequest
} from '@amwager/util-ts';

// Local file
import { HttpService } from '../services';



// TODO: write tests
@Injectable()
export class ToteAccountAPIService {
  private readonly API_URL = '/api/ToteAccount';

  constructor(
    private readonly httpService: HttpService,
  ) {}


  /**
   * GET API
   */

  /**
   * @description
   * Allows Affiliates to withdrawal money from authorized accounts.
   * (Limited to old affiliates)
   *
   * @param requests IAffiliateWithdrawRequest
   * @returns Observable<IAffiliateWithdrawResponseModel>
   */
  affiliateWithdrawal$(requests: IAffiliateWithdrawRequest)
    : Observable<IAffiliateWithdrawResponseModel> {
    return this.httpService.get$({
      api: `${this.API_URL}/AffiliateWithdrawal`,
      params: this.httpService.objToHttpParams(requests as unknown as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Allows an affiliate to request the balance of an account.
   * (Limited to old Affiliates)
   *
   * @param account_id string
   * @param pin string
   * @param reference_id string
   * @returns Observable<IAffiliateBalanceResponseModel>
   */
  affiliateBalance$(account_id: string, pin: string, reference_id: string)
    : Observable<IAffiliateBalanceResponseModel> {
    return this.httpService.get$({
      api: `${this.API_URL}/AffiliateBalance`,
      params: this.httpService.objToHttpParams({
        account_id,
        pin,
        reference_id
      })
    });
  }

  /**
   * @description
   * Allows affliates to deposit money into an account.
   * (Limited to Old Affiliates)
   *
   * @param requests IAffiliateWithdrawRequest
   * @returns Observable<IAffiliateDepositResponseModel>
   */
  affiliateDeposit$(requests: IAffiliateWithdrawRequest)
    : Observable<IAffiliateDepositResponseModel> {
    return this.httpService.get$({
      api: `${this.API_URL}/AffiliateDeposit`,
      params: this.httpService.objToHttpParams(requests as unknown as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Get a list of all transactions and wagers that match the specified query.
   *
   * @param request IGetAccountReportExRequest
   * @returns Observable<IGetAccountStatementReportResponse>
   */
  getAccountReportEx$(request: IGetAccountReportExRequest)
    : Observable<IGetAccountStatementReportResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAccountReportEx`,
      params: this.httpService.objToHttpParams({
        'request.startDate': request?.startDate
          ? moment.utc( new Date(request?.startDate) ).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        'request.endDate': request?.endDate
          ? moment.utc( new Date(request?.endDate) ).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        'request.eventId': request?.eventId || undefined,
        'request.poolId': request?.poolId || undefined,
        'request.modifier': request?.modifier || undefined,
        'request.minAmount': request?.minAmount || undefined,
        'request.maxAmount': request?.maxAmount || undefined,
        'request.status': request?.status || undefined,
        'request.isCancelled': request?.isCancelled || undefined,
        'request.offset': request?.offset || 0,
        'request.next': request?.next || 100
      } as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Get last 4 of adirondack account for display to user.
   *
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getAdirondackAccount$(): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAdirondackAccount`,
    });
  }

  /**
   * @description
   * Get customer's ACH bank accounts
   *
   * @returns Observable<IGetAllACHAccountsResponse>
   */
  getAllACHAccounts$(): Observable<IGetAllACHAccountsResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllACHAccounts`,
    });
  }

  /**
   * @description
   * Get report on of all dutch wagers that matches specified query.
   *
   * @param requests IGetDutchAccountReportExRequest
   * @returns Observable<IGetDutchParentStatementReportResponseEx>
   */
  getDutchAccountReportEx$(requests: IGetDutchAccountReportExRequest)
    : Observable<IGetDutchParentStatementReportResponseEx> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetDutchAccountReportEx`,
      params: this.httpService.objToHttpParams(requests as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Get a list of all tickets in a specific dutch wager.
   *
   * @param parentticketid string
   * @returns Observable<IGetDutchParentTicketsReportResponseEx>
   */
  getDutchAccountTicketsReportEx$(parentticketid: string)
    : Observable<IGetDutchParentTicketsReportResponseEx> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetDutchAccountTicketsReportEx`,
      params: this.httpService.objToHttpParams({
        parentticketid
      })
    });
  }

  /**
   * @description
   * Get report on all tickets submitted as part of a file upload
   *
   * @param requests IGetFileUploadReportExRequest
   * @returns Observable<IGetFileUploadStatementReportResponseEx>
   */
  getFileUploadReportEx$(requests: IGetFileUploadReportExRequest)
    : Observable<IGetFileUploadStatementReportResponseEx> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetFileUploadReportEx`,
      params: this.httpService.objToHttpParams(requests as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Get report on all tickets submitted as part of a file upload
   *
   * @param request IGetFileUploadTicksetReportExRequest
   * @returns Observable<IGetFileUploadTicketsReportResponseEx>
   */
  getFileUploadTicksetReportEx$(request: IGetFileUploadTicksetReportExRequest)
    : Observable<IGetFileUploadTicketsReportResponseEx> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetFileUploadTicksetReportEx`,
      params: this.httpService.objToHttpParams({
        runId: request?.runId,
        fileUploadName: request?.fileUploadName,
        offset: request?.offset,
        next: request?.next
      })
    });
  }

  /**
   * @description
   * Get the ach limits of the requesting customer
   *
   * @returns Observable<IGetCustomerLimitResponse>
   */
  getFundingLimits$(): Observable<IGetCustomerLimitResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetFundingLimits`
    });
  }

  /**
   * @description
   * Get any files that are currently being uploaded
   *
   * @returns Observable<IGetOnGoingFileUploadsResponse>
   */
  getOnGoingFileUploads$(): Observable<IGetOnGoingFileUploadsResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetOnGoingFileUploads`,
    });
  }

  /**
   * @description
   * Get the address of the logged in user
   *
   * @returns Observable<IGetToteAccountAddress>
   */
  getToteAccountAddress$(): Observable<IGetToteAccountAddress> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetToteAccountAddress`
    });
  }

  /**
   * @description
   * Get the balance of the logged in user
   *
   * @returns Observable<IToteAccountBalanceResponse>
   */
  getToteAccountBalance$(): Observable<IToteAccountBalanceResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetToteAccountBalance`
    });
  }

  /**
   * @description
   * Get list of uploaded files for specified query.
   *
   * @param requests IGetUploadedFilesRequest
   * @returns Observable<IGetUploadedFilesResponse>
   */
  getUploadedFiles$(requests: IGetUploadedFilesRequest)
    : Observable<IGetUploadedFilesResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetUploadedFiles`,
      params: this.httpService.objToHttpParams(requests as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Get reports for wagering
   *
   * @param requests IGetWagerReportRequest
   * @returns Observable<IGetWagerReportResponse>
   */
  getWagerReport$(requests: IGetWagerReportRequest)
    : Observable<IGetWagerReportResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetWagerReport`,
      params: this.httpService.objToHttpParams(requests as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Logout a user from the system.
   *
   * @returns Observable<IBaseResponseSlim>
   */
  logou$(): Observable<IBaseResponseSlim> {
    return this.httpService.get$({
      api: `${this.API_URL}/Logout`
    });
  }

  /**
   * @description
   * Get all account data for the logged in user.
   *
   * @returns Observable<IToteAccountCallResponse>
   */
  queryToteAccount$(): Observable<IToteAccountCallResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/QueryToteAccount`
    });
  }

  /**
   * @description
   * Generate a renew password link and send to email if email exists.
   *
   * @param email string
   * @param uiType number
   * @returns Observable<IResponseModel<IBaseResponseSlim>>
   */
  renewPasswordLink$(email: string, uiType?: number)
    : Observable<IResponseModel<IBaseResponseSlim>> {
    return this.httpService.get$({
      api: `${this.API_URL}/RenewPasswordLink`,
      params: this.httpService.objToHttpParams({
        email,
        uiType
      })
    });
  }


  /**
   * POST API
   */

  /**
   * @description
   * Withdraws money from the user account to be sent to user manually
   * via administration.
   *
   * @param requests IManualTransactionRequest
   * @returns Observable<IBaseResponseSlim>
   */
  addManualWithdrawlRequest$(requests: IManualTransactionRequest)
    : Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/AddManualWithdrawlRequest`,
      body: requests
      // body: { requests }
    })
  }

  /**
   * @description
   * Requests money be deposited into the account once reviewed and approved
   * by Administration.
   *
   * @param requests IManualTransactionRequest
   * @returns Observable<IBaseResponseSlim>
   */
  addManualDepositRequest$(requests: IManualTransactionRequest)
    : Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/AddManualDepositRequest`,
      body: { requests }
    })
  }

  /**
   * @description
   * Make a deposit via Automated Clearing House bank system.
   *
   * @param requests IACHRequest
   * @returns Observable<IBaseResponseSlim>
   */
  aCHDeposit$(requests: IACHRequest): Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/ACHDeposit`,
      body: requests
    })
  }

  /**
   * @description
   * Make a withdrawal using Automated Clearing House bank system.
   *
   * @param requests IACHRequest
   * @returns Observable<IBaseResponseSlim>
   */
  aCHWithdrawal$(requests: IACHRequest): Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/ACHWithdrawal`,
      body: requests
    })
  }

  /**
   * @description
   * Add ACH bank account details
   *
   * @param requests IAddACHAccountRequest
   * @returns Observable<IBaseResponseSlim>
   */
  addACHAccount$(requests: IAddACHAccountRequest)
    : Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/AddACHAccount`,
      body: requests
    })
  }

  /**
   * @description
   * Add new account
   * Notes: Contact support for affiliate usage.
   *
   * @param newAccountRequest INewAccountRequest
   * @param runIdCheck string
   * @param sendNotification string
   * @returns Observable<IToteAddAccountCallResponse>
   */
  adminAddToteAccount$(
    newAccountRequest: INewAccountRequest,
    runIdCheck?: string,
    sendNotification?: string
  ): Observable<IToteAddAccountCallResponse> {
    return this.httpService.post$({
      api: `${this.API_URL}/AdminAddToteAccount`,
      params: this.httpService.objToHttpParams({
        runIdCheck,
        sendNotification
      }),
      body: newAccountRequest
    })
  }

  /**
   * @description
   * Make a deposit via ACH bank system specifically through Adirondack.
   *
   * @param depositAmount number
   * @returns Observable<IBaseResponseSlim>
   */
  adirondackDeposit$(depositAmount: number): Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/AdirondackDeposit`,
      params: this.httpService.objToHttpParams({
        depositAmount
      }),
    })
  }

  /**
   * @description
   * Make a deposit via ACH bank system specifically through Adirondack.
   *
   * @param withdrawAmount number
   * @returns Observable<IBaseResponseSlim>
   */
  adirondackWithdraw$(withdrawAmount: number): Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/AdirondackWithdraw`,
      params: this.httpService.objToHttpParams({
        withdrawAmount
      }),
    })
  }

  /**
   * @description
   * Make a deposit using various Crypto Currencies through BitPay.
   *
   * @param requests ICryptoDepositRequest
   * @returns Observable<ICryptoDepositResponse>
   */
  cryptoDeposit$(requests: ICryptoDepositRequest)
    : Observable<ICryptoDepositResponse> {
    return this.httpService.post$({
      api: `${this.API_URL}/CryptoDeposit`,
      body: requests
    })
  }

  /**
   * @description
   * Make a deposit using a Greendot MoneyPak card.
   *
   * @param requests IMoneyPakDepositRequest
   * @returns Observable<IBaseResponseSlim>
   */
  moneyPakDeposit$(requests: IMoneyPakDepositRequest)
    : Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/MoneyPakDeposit`,
      body: requests
    })
  }


  /**
   * PUT API
   */

  /**
   * @description
   * Update the password of a user
   *
   * @param updatePasswordRequest IUpdatePasswordRequest
   * @returns Observable<IResponseModel<IBaseResponseSlim>>
   */
  updatePassword$(updatePasswordRequest: IUpdatePasswordRequest)
    : Observable<IResponseModel<IBaseResponseSlim>> {
    return this.httpService.put$({
      api: `${this.API_URL}/UpdatePassword`,
      body: {
        ReplaceIdentifier: updatePasswordRequest.ReplaceIdentifier,
        NewPassword: updatePasswordRequest.NewPassword
      }
    })
  }

  /**
   * @description
   * Update ACH bank account details
   *
   * @param requests IUpdateACHAccountRequest
   * @returns Observable<IBaseResponseSlim>
   */
  updateACHAccount$(requests: IUpdateACHAccountRequest)
    : Observable<IBaseResponseSlim> {
    return this.httpService.put$({
      api: `${this.API_URL}/UpdateACHAccount`,
      body: requests
    })
  }


  /**
   * DELETE API
   */

  /**
   * @description
   * Delete a preference of an AmWest account
   *
   * @param prefDeleteRequest IAmWestDeletePrefRequest
   * @returns Observable<IAmWestDeletePrefResponse>
   */
  deleteAmWestAccountPref$(prefDeleteRequest: IAmWestDeletePrefRequest)
    : Observable<IAmWestDeletePrefResponse> {
    return this.httpService.delete$({
      api: `${this.API_URL}/DeleteAmWestAccountPref`,
      body: prefDeleteRequest
    })
  }

  /**
   * @description
   * Delete ACH bank account details
   *
   * @param requests IDeleteACHAccountRequest
   * @returns Observable<IBaseResponseSlim>
   */
  deleteACHAccount$(requests: IDeleteACHAccountRequest)
    : Observable<IBaseResponseSlim> {
    return this.httpService.delete$({
      api: `${this.API_URL}/DeleteACHAccount`,
      body: requests
    })
  }

}
